import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { isMobileDevice } from './utils/mobileUtils';
import {
  Rocket,
  Zap,
  Brain,
  Timer,
  ShieldCheck,
  LineChart,
  Stars,
  ArrowRight,
  Award,
  Users,
  BookOpenCheck,
  Target,
  CheckCircle,
  PlayCircle,
  Mail,
  KeyRound,
  Shield,
  CheckCircle2,
  LogIn,
} from "lucide-react";
import EmailCaptureModal from "./components/EmailCaptureModal.jsx";
import { createClient } from "@supabase/supabase-js";

// Modern CSS animations
const styles = `
  @keyframes gentle-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(255, 105, 180, 0.4), 0 0 40px rgba(139, 92, 246, 0.2); }
    50% { box-shadow: 0 0 30px rgba(255, 105, 180, 0.6), 0 0 60px rgba(139, 92, 246, 0.3); }
  }
  
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes pulse-glow {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
  
  @keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .gentle-glow {
    animation: gentle-glow 3s ease-in-out infinite;
  }
  
  .shimmer-text {
    background: linear-gradient(90deg, 
      rgba(255,255,255,0.8) 0%, 
      rgba(255,255,255,1) 50%, 
      rgba(255,255,255,0.8) 100%);
    background-size: 200% auto;
    animation: shimmer 3s linear infinite;
    -webkit-background-clip: text;
    background-clip: text;
  }
  
  .floating {
    animation: float 6s ease-in-out infinite;
  }
  
  .pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }
  
  .gradient-shift {
    background-size: 200% 200%;
    animation: gradient-shift 4s ease infinite;
  }
  
  .hover-lift {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .hover-lift:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('landing-animations')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'landing-animations';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

/**
 * AI Tester Quiz – High-converting Landing (single-file React)
 * -----------------------------------------------------------
 * • TailwindCSS utility classes for styling (no external CSS required)
 * • Framer Motion for micro-animations
 * • Mobile-first, accessible, SEO-friendly
 * • Two access modes:
 *    - TYPEFORM (lead form → redirect to demo)
 *    - SUPABASE AUTH (Google + magic link + OTP)
 * • “Legal” opens as modals via ?p=privacy|terms (no router required)
 */

// -----------------------------
// Safe ENV helpers (browser friendly)
// -----------------------------
function getEnvValue(keys, fallback = "") {
  try {
    if (typeof window !== "undefined" && window.__ENV__) {
      for (const k of keys) if (window.__ENV__[k]) return String(window.__ENV__[k]);
    }
  } catch {}
  try {
    // eslint-disable-next-line no-undef
    if (typeof import.meta !== "undefined" && import.meta.env) {
      // eslint-disable-next-line no-undef
      for (const k of keys) if (import.meta.env[k]) return String(import.meta.env[k]);
    }
  } catch {}
  try {
    // eslint-disable-next-line no-undef
    if (typeof process !== "undefined" && process.env) {
      // eslint-disable-next-line no-undef
      for (const k of keys) if (process.env[k]) return String(process.env[k]);
    }
  } catch {}
  return fallback;
}

// -----------------------------
// Config
// -----------------------------
const TYPEFORM_URL = "https://form.typeform.com/to/yJqRUPoR"; // primary CTA target
const DEMO_URL = "/demoquiz"; // demo quiz (after survey) - relative URL to work with any domain
const GA_ID = getEnvValue(["NEXT_PUBLIC_GA_ID", "GA_ID", "REACT_APP_GA_ID", "VITE_GA_ID"], "");
const META_PIXEL_ID = getEnvValue([
  "NEXT_PUBLIC_META_PIXEL_ID",
  "META_PIXEL_ID",
  "REACT_APP_META_PIXEL_ID",
  "VITE_META_PIXEL_ID",
], "");

const AUTH_MODE = getEnvValue([
  "NEXT_PUBLIC_AUTH_MODE",
  "AUTH_MODE",
  "REACT_APP_AUTH_MODE",
  "VITE_AUTH_MODE",
], "typeform").toLowerCase(); // 'typeform' | 'supabase'
const AUTH_REDIRECT = getEnvValue([
  "NEXT_PUBLIC_AUTH_REDIRECT",
  "AUTH_REDIRECT",
  "REACT_APP_AUTH_REDIRECT",
  "VITE_AUTH_REDIRECT",
], "demo"); // 'demo' | 'callback'
const SUPABASE_URL = getEnvValue([
  "NEXT_PUBLIC_SUPABASE_URL",
  "SUPABASE_URL",
  "REACT_APP_SUPABASE_URL",
  "VITE_SUPABASE_URL",
]);
const SUPABASE_ANON_KEY = getEnvValue([
  "NEXT_PUBLIC_SUPABASE_ANON_KEY",
  "SUPABASE_ANON_KEY",
  "REACT_APP_SUPABASE_ANON_KEY",
  "VITE_SUPABASE_ANON_KEY",
]);

function getSupabase() {
  try {
    if (SUPABASE_URL && SUPABASE_ANON_KEY) {
      return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: { persistSession: true, autoRefreshToken: true },
      });
    }
  } catch {}
  return null;
}

function currentUTMQuery() {
  if (typeof window === "undefined") return "";
  const keep = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "lang",
    "ad_id",
    "creative_id",
  ];
  const src = new URLSearchParams(window.location.search);
  const out = new URLSearchParams();
  keep.forEach((k) => {
    const v = src.get(k);
    if (v) out.set(k, v);
  });
  return out.toString() ? `?${out.toString()}` : "";
}

function withUTM(url) {
  const q = currentUTMQuery();
  if (!q) return url;
  try {
    const u = new URL(url);
    const extra = new URLSearchParams(q.replace(/^\?/, ""));
    extra.forEach((v, k) => u.searchParams.set(k, v));
    return u.toString();
  } catch {
    return url + q;
  }
}

// -----------------------------
// Crypto helpers + attribution
// -----------------------------
async function sha256Hex(value) {
  try {
    if (!value) return null;
    const data = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

function getFBPFBC() {
  let fbc = null, fbp = null;
  try {
    const sp = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    if (sp.get("fbclid")) fbc = `fb.1.${Date.now()}.${sp.get("fbclid")}`;
    const m = document.cookie.match(/_fbp=([^;]+)/);
    if (m) fbp = m[1];
  } catch {}
  return { fbc, fbp };
}

function getUtmObject() {
  const q = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const obj = {};
  for (const k of q.keys()) {
    if (k.startsWith("utm_") || ["lang", "ad_id", "creative_id"].includes(k)) obj[k] = q.get(k);
  }
  return obj;
}

function getAuthRedirectTarget() {
  if (AUTH_REDIRECT === "callback" && typeof window !== "undefined") {
    const origin = window.location.origin || "";
    return `${origin}/auth/callback${currentUTMQuery()}`;
  }
  
  if (typeof window !== "undefined" && window.location.hostname === 'localhost') {
    return `${window.location.origin}/demoquiz`;
  }
  
  return withUTM(DEMO_URL);
}

// -----------------------------
// Supabase logging helpers
// -----------------------------
async function upsertLead(supabase, { email, emailHash, method, consentMarketing, consentTerms, status }) {
  if (!supabase) return;
  try {
    const utm = getUtmObject();
    const payload = {
      email_hash: emailHash || null,
      email: consentMarketing ? email : null, // store plain email only if marketing consent
      method: method || null,
      consent_marketing: !!consentMarketing,
      consent_terms: !!consentTerms,
      status: status || "started",
      utm,
    };
    await supabase.from("leads").upsert(payload, { onConflict: "email_hash" });
  } catch (e) {
    console.warn("Lead upsert failed (non-blocking)", e?.message);
  }
}

async function insertEvent(supabase, { name, data }) {
  if (!supabase) return;
  try {
    await supabase.from("events").insert({ name, data: data || {}, ts: new Date().toISOString() });
  } catch (e) {
    console.warn("Event insert failed (non-blocking)", e?.message);
  }
}

async function sendMetaCapi(eventName, email) {
  try {
    const emailHash = await sha256Hex(String(email || "").trim().toLowerCase());
    if (!emailHash) return;
    const { fbc, fbp } = getFBPFBC();
    const body = {
      event_name: eventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id: `${eventName}-${emailHash}-${Date.now()}`,
      user_data: { em: emailHash, fbc, fbp },
      utm: getUtmObject(),
    };
    fetch("/api/meta-capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

// -----------------------------
// Assets (logo + optional screenshots)
// -----------------------------
const LOGO_URL = getEnvValue(["NEXT_PUBLIC_LOGO_URL", "LOGO_URL", "VITE_LOGO_URL"], "https://i.ibb.co/WNYxLd8k/IMG-2260.png");
const SCREEN_1 = getEnvValue(["NEXT_PUBLIC_SCREEN_1", "SCREEN_1", "VITE_SCREEN_1"], "");
const SCREEN_2 = getEnvValue(["NEXT_PUBLIC_SCREEN_2", "SCREEN_2", "VITE_SCREEN_2"], "");
const SCREEN_3 = getEnvValue(["NEXT_PUBLIC_SCREEN_3", "SCREEN_3", "VITE_SCREEN_3"], "");
const SCREEN_4 = getEnvValue(["NEXT_PUBLIC_SCREEN_4", "SCREEN_4", "VITE_SCREEN_4"], "");
const ASSETS = {
  logo: LOGO_URL,
  screens: [SCREEN_1, SCREEN_2, SCREEN_3, SCREEN_4].filter(Boolean),
};

// -----------------------------
// UI primitives
// -----------------------------
const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-7xl px-5 md:px-8 ${className}`}>{children}</div>
);

const Glow = ({ className = "" }) => (
  <div aria-hidden className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className}`}>
    <div className="absolute -top-40 right-20 h-96 w-96 rounded-full bg-pink-500/25 blur-3xl" />
    <div className="absolute -bottom-32 left-10 h-96 w-96 rounded-full bg-violet-700/20 blur-3xl" />
    <div className="absolute top-1/3 -left-32 h-80 w-80 rounded-full bg-amber-300/20 blur-3xl" />
  </div>
);

const Badge = ({ icon: Icon, children }) => (
  <motion.span 
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    whileHover={{ scale: 1.05 }}
    className="floating inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur hover-lift"
  >
    {Icon && <Icon className="h-4 w-4 text-fuchsia-300" />} {children}
  </motion.span>
);

const Logo = () => (
  <div className="gentle-glow grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-fuchsia-500 via-violet-600 to-cyan-500 text-white shadow-lg overflow-hidden gradient-shift">
    {ASSETS.logo ? (
      <img src={ASSETS.logo} alt="AI.Nastasiia" className="h-full w-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
    ) : (
      <Brain className="h-5 w-5" />
    )}
  </div>
);

const PrimaryCTA = ({ large = false, className = "", onClick, label = "Get Free Demo" }) => (
  <motion.button
    onClick={onClick}
    whileTap={{ scale: 0.98 }}
    whileHover={{ y: -2, boxShadow: "0 20px 40px rgba(255, 105, 180, 0.4)" }}
    className={`gentle-glow group relative inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-pink-500 to-fuchsia-600 px-6 py-4 font-bold text-white shadow-lg shadow-fuchsia-700/30 ring-0 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/60 hover:shadow-2xl gradient-shift ${
      large ? "px-8 py-5 text-lg md:text-xl min-w-[280px]" : "text-base min-w-[200px]"
    } ${className}`}
  >
    <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent opacity-20" />
    <span className="absolute inset-0 rounded-2xl bg-white/0 group-hover:bg-white/10 transition-colors duration-300" />
    <span className="relative flex items-center gap-3 font-bold tracking-wide">
      <Zap className="h-5 w-5 group-hover:animate-pulse" />
      {label}
      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
    </span>
    {large && (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-pulse"
      >
        FREE
      </motion.div>
    )}
  </motion.button>
);

// -----------------------------
// Typeform Modal
// -----------------------------
const TypeformModal = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur"
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          className="relative mx-4 w-[min(1100px,96vw)] overflow-hidden rounded-2xl border border-white/10 bg-neutral-900 shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div className="flex items-center gap-3 text-white/90">
              <Timer className="h-5 w-5" />
              <p className="text-sm md:text-base">
                30‑second access check. A few fun questions → we unlock the demo right away. No spam.
              </p>
            </div>
            <button onClick={onClose} className="rounded-md bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20">
              Close
            </button>
          </div>
          <div className="h-[70vh] w-full">
            <iframe
              title="Typeform – AI Tester Access"
              src={withUTM(TYPEFORM_URL)}
              className="h-full w-full"
              allow="camera; microphone; autoplay; encrypted-media"
            />
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-white/10 px-5 py-3 text-xs text-white/60">
            <span>
              Trouble with the embed?{" "}
              <a href={withUTM(TYPEFORM_URL)} target="_blank" rel="noopener noreferrer" className="text-fuchsia-300 underline hover:text-fuchsia-200">
                Open in a new tab
              </a>
            </span>
            <span>After submission we’ll redirect you to the demo: {withUTM(DEMO_URL)}</span>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// -----------------------------
// Supabase Auth Modal (optional)
// -----------------------------
const AuthModal = ({ open, onClose, supabase, onTracked }) => {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("choice"); // 'choice' | 'email' | 'code' | 'sent' | 'verifying'
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [consentTerms, setConsentTerms] = useState(false); // required
  const [consentMarketing, setConsentMarketing] = useState(false); // optional

  useEffect(() => {
    if (!open) {
      setEmail("");
      setCode("");
      setStep("choice");
      setErr("");
      setConsentTerms(false);
      setConsentMarketing(false);
    }
  }, [open]);

  const redirectTo = getAuthRedirectTarget();
  const track = (name, params = {}) => { try { onTracked && onTracked(name, params); } catch {} };

  const preLead = async (method) => {
    try {
      const emailHash = await sha256Hex(String(email || "").trim().toLowerCase());
      await upsertLead(supabase, { email, emailHash, method, consentMarketing, consentTerms, status: "started" });
      await insertEvent(supabase, { name: "auth_start", data: { method, consentMarketing, consentTerms } });
    } catch {}
  };

  const sendMagic = async () => {
    setErr("");
    if (!consentTerms) { setErr("Please agree to the Terms & Privacy to continue."); return; }
    track("auth_start", { method: "magic_link" });
    await preLead("magic_link");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: true },
      });
      if (error) throw error;
      setStep("sent");
      track("auth_email_sent", { method: "magic_link" });
    } catch (e) {
      setErr(e?.message || "Could not send the email. Try again or use code.");
    }
  };

  const sendOtpCode = async () => {
    setErr("");
    if (!consentTerms) { setErr("Please agree to the Terms & Privacy to continue."); return; }
    track("auth_start", { method: "otp" });
    await preLead("otp");
    try {
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
      if (error) throw error;
      setStep("code");
      track("otp_sent", {});
    } catch (e) {
      setErr(e?.message || "Could not send the code. Try again.");
    }
  };

  const verifyCode = async () => {
    setErr("");
    setStep("verifying");
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token: code, type: "email" });
      if (error) throw error;
      track("auth_verified", { method: "otp" });
      const emailHash = await sha256Hex(String(email || "").trim().toLowerCase());
      await upsertLead(supabase, { email, emailHash, method: "otp", consentMarketing, consentTerms, status: "verified" });
      await insertEvent(supabase, { name: "auth_verified", data: { method: "otp" } });
      if (consentMarketing) await sendMetaCapi("Lead", email);
      const demoUrl = window.location.hostname === 'localhost' 
        ? `${window.location.origin}/demoquiz`
        : withUTM(DEMO_URL);
      window.location.href = demoUrl;
    } catch (e) {
      setErr(e?.message || "Invalid code. Please try again.");
      setStep("code");
    }
  };

  const signInGoogle = async () => {
    setErr("");
    if (!consentTerms) { setErr("Please agree to the Terms & Privacy to continue."); return; }
    track("auth_start", { method: "google" });
    await preLead("google");
    try {
      const preferRedirect = true;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo, queryParams: { prompt: "select_account" }, skipBrowserRedirect: !preferRedirect },
      });
      if (error) throw error;
      if (!preferRedirect) track("auth_verified", { method: "google" });
    } catch (e) {
      setErr(e?.message || "Google sign-in failed. Try magic link instead.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur">
          <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }} className="relative mx-4 w-[min(560px,96vw)] overflow-hidden rounded-2xl border border-white/10 bg-neutral-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-3 text-white/90">
                <LogIn className="h-5 w-5" />
                <p className="text-sm md:text-base">Sign in to get your demo link. No passwords.</p>
              </div>
              <button onClick={onClose} className="rounded-md bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20">Close</button>
            </div>

            <div className="space-y-4 p-5">
              {err && (<div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{err}</div>)}

              {/* Consent row */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/70">
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={consentTerms} onChange={(e) => setConsentTerms(e.target.checked)} />
                  <span>
                    I agree to the <a className="underline" href="?p=privacy">Privacy Policy</a> and <a className="underline" href="?p=terms">Terms</a>.
                    <span className="ml-1 text-white/50">(required)</span>
                  </span>
                </label>
                <label className="mt-2 flex items-start gap-2">
                  <input type="checkbox" checked={consentMarketing} onChange={(e) => setConsentMarketing(e.target.checked)} />
                  <span> I agree to receive training tips & occasional updates by email. <span className="text-white/50">(optional)</span></span>
                </label>
              </div>

              {step === "choice" && (
                <div className="grid gap-3">
                  <button disabled={!consentTerms} onClick={signInGoogle} className={`inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm font-medium text-white ${consentTerms ? "bg-white/10 hover:bg-white/15" : "bg-white/5 opacity-50"}`}>
                    <img alt="Google" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="h-5 w-5" />
                    Continue with Google
                  </button>
                  <button onClick={() => setStep("email")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white hover:bg-white/10">
                    <Mail className="h-5 w-5" />
                    Continue with email (magic link)
                  </button>
                  <button onClick={() => setStep("code")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-white hover:bg-white/10">
                    <KeyRound className="h-5 w-5" />
                    Use a 6‑digit code instead
                  </button>
                  <div className="flex items-center gap-2 pt-1 text-xs text-white/50">
                    <Shield className="h-4 w-4" /> No spam. Unsubscribe anytime.
                  </div>
                </div>
              )}

              {step === "email" && (
                <div className="grid gap-3">
                  <label className="text-sm text-white/80">Work email</label>
                  <input type="email" inputMode="email" autoFocus placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border border-white/10 bg-neutral-800 px-3 py-2 text-white placeholder-white/40 outline-none focus:border-fuchsia-400/50" />
                  <PrimaryCTA label="Send me the link" onClick={sendMagic} />
                  <button onClick={() => setStep("code")} className="text-sm text-white/70 underline underline-offset-4">Prefer a 6‑digit code?</button>
                </div>
              )}

              {step === "sent" && (
                <div className="grid place-items-center gap-2 py-4 text-center text-white/80">
                  <CheckCircle2 className="h-8 w-8 text-fuchsia-300" />
                  <div className="text-sm">Email sent to <span className="font-semibold text-white">{email}</span>.</div>
                  <div className="text-xs text-white/60">Didn’t get it? Check spam or switch to code below.</div>
                  <button onClick={() => setStep("code")} className="mt-2 text-sm text-white/70 underline underline-offset-4">Use a 6‑digit code instead</button>
                </div>
              )}

              {step === "code" && (
                <div className="grid gap-3">
                  <label className="text-sm text-white/80">Email</label>
                  <input type="email" inputMode="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border border-white/10 bg-neutral-800 px-3 py-2 text-white placeholder-white/40 outline-none focus:border-fuchsia-400/50" />
                  <label className="text-sm text-white/80">6‑digit code</label>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="rounded-xl border border-white/10 bg-neutral-800 px-3 py-2 text-white placeholder-white/40 outline-none focus:border-fuchsia-400/50" />
                  <PrimaryCTA label={step === "verifying" ? "Verifying…" : "Verify & enter demo"} onClick={verifyCode} />
                  <button onClick={sendOtpCode} className="text-sm text-white/70 underline underline-offset-4">Resend code</button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-white/10 px-5 py-3 text-xs text-white/60">
              <span>
                Prefer the short form?{" "}
                <a href={withUTM(TYPEFORM_URL)} target="_blank" rel="noopener noreferrer" className="text-fuchsia-300 underline hover:text-fuchsia-200">Open Typeform in a new tab</a>
              </span>
              <span>After sign‑in we’ll redirect you to the demo: {withUTM(DEMO_URL)}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// -----------------------------
// /auth/callback view (optional)
// -----------------------------
function AuthCallbackView({ supabase }) {
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const email = data?.user?.email;
        if (email) {
          const emailHash = await sha256Hex(String(email).trim().toLowerCase());
          await upsertLead(supabase, { email, emailHash, method: "google", consentMarketing: true, consentTerms: true, status: "verified" });
          await insertEvent(supabase, { name: "auth_verified", data: { method: "google" } });
          await sendMetaCapi("Lead", email);
        }
      } catch {}
      const demoUrl = window.location.hostname === 'localhost' 
        ? `${window.location.origin}/demoquiz`
        : withUTM(DEMO_URL);
      window.location.replace(demoUrl);
    })();
  }, [supabase]);

  return (
    <main className="grid min-h-screen place-items-center bg-neutral-950 text-white">
      <div className="text-center">
        <div className="mb-3 text-sm text-white/60">Finishing sign‑in…</div>
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    </main>
  );
}

// -----------------------------
// Legal modal via ?p=privacy|terms
// -----------------------------
const LegalModal = ({ open, page, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
        <motion.div className="relative mx-4 w-[min(900px,96vw)] overflow-hidden rounded-2xl border border-white/10 bg-neutral-900 shadow-2xl" initial={{y:24,opacity:0}} animate={{y:0,opacity:1}} exit={{y:24,opacity:0}}>
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 text-white">
            <div className="font-semibold">{page === 'privacy' ? 'Privacy Policy' : 'Terms of Use'}</div>
            <button onClick={onClose} className="rounded-md bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20">Close</button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-6 text-white/80">
            {page === 'privacy' ? (
              <div className="prose prose-invert prose-sm max-w-none">
                <p className="text-lg font-medium mb-6"><strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                
                <p><strong>Short version:</strong> We collect your email to provide our AI Tester training services and send you useful materials if you consent. We're GDPR compliant and respect your privacy rights.</p>
                
                <h3>1. Data Controller</h3>
                <p>AI.Nastasiia ("we," "us," "our") is the data controller responsible for your personal data. We are located in the European Union and comply with EU data protection laws.</p>
                <p><strong>Contact:</strong> contact@ainastasia.ai</p>
                
                <h3>2. Information We Collect</h3>
                <h4>Personal Information You Provide:</h4>
                <ul>
                  <li><strong>Contact Information:</strong> Email address (required), first name (optional)</li>
                  <li><strong>Communication Data:</strong> Messages you send us, survey responses, feedback</li>
                  <li><strong>Marketing Preferences:</strong> Your consent choices for marketing communications</li>
                </ul>
                
                <h4>Information Collected Automatically:</h4>
                <ul>
                  <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent, clicks, quiz progress and results</li>
                  <li><strong>Analytics Data:</strong> Via Google Analytics and similar services (with your consent)</li>
                  <li><strong>Marketing Data:</strong> UTM parameters, referral sources, campaign performance</li>
                </ul>
                
                <h3>3. How We Use Your Information</h3>
                <p>We process your personal data for the following purposes:</p>
                
                <h4>Service Delivery (Legal Basis: Contract Performance)</h4>
                <ul>
                  <li>Provide access to our AI Tester quiz and training materials</li>
                  <li>Deliver personalized learning experiences and progress tracking</li>
                  <li>Send service-related communications and updates</li>
                  <li>Process payments for premium services</li>
                </ul>
                
                <h4>Marketing (Legal Basis: Consent)</h4>
                <ul>
                  <li>Send newsletters, training tips, and product updates</li>
                  <li>Personalize marketing communications based on your interests</li>
                  <li>Conduct market research and surveys</li>
                </ul>
                
                <h4>Analytics and Improvement (Legal Basis: Legitimate Interests)</h4>
                <ul>
                  <li>Analyze website usage and user behavior</li>
                  <li>Improve our services and develop new features</li>
                  <li>Measure marketing campaign effectiveness</li>
                  <li>Ensure security and prevent fraud</li>
                </ul>
                
                <h3>4. Data Sharing and Third Parties</h3>
                <p>We share your data only in limited circumstances:</p>
                
                <h4>Service Providers:</h4>
                <ul>
                  <li><strong>SendGrid:</strong> Email delivery service (US with EU adequacy decision)</li>
                  <li><strong>Google Analytics:</strong> Website analytics (data processing agreement in place)</li>
                  <li><strong>Gumroad:</strong> Payment processing for premium services</li>
                  <li><strong>Cloud Hosting:</strong> Secure data storage and application hosting</li>
                </ul>
                
                <h4>Legal Requirements:</h4>
                <p>We may disclose your information when required by law, legal process, or to protect our rights and safety.</p>
                
                <h3>5. International Data Transfers</h3>
                <p>Some of our service providers are located outside the EEA. We ensure appropriate safeguards through:</p>
                <ul>
                  <li>EU-US Data Privacy Framework participation</li>
                  <li>Standard Contractual Clauses (SCCs)</li>
                  <li>Data Processing Agreements with GDPR compliance</li>
                </ul>
                
                <h3>6. Data Retention</h3>
                <ul>
                  <li><strong>Active Users:</strong> Data retained while you use our services</li>
                  <li><strong>Inactive Users:</strong> Personal data deleted after 3 years of inactivity</li>
                  <li><strong>Marketing Data:</strong> Retained until you withdraw consent</li>
                  <li><strong>Legal Requirements:</strong> Some data may be retained longer for legal compliance</li>
                </ul>
                
                <h3>7. Your Rights (GDPR)</h3>
                <p>As an EU resident, you have the following rights:</p>
                <ul>
                  <li><strong>Access:</strong> Request copies of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Erasure:</strong> Delete your personal data ("right to be forgotten")</li>
                  <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                  <li><strong>Restriction:</strong> Limit how we process your data</li>
                  <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Withdraw Consent:</strong> Revoke consent for marketing or analytics</li>
                  <li><strong>Complaint:</strong> File a complaint with your data protection authority</li>
                </ul>
                
                <p>To exercise these rights, contact us at <a href="mailto:contact@ainastasia.ai" className="text-blue-400 hover:text-blue-300">contact@ainastasia.ai</a></p>
                
                <h3>8. Data Security</h3>
                <p>We implement appropriate technical and organizational measures to protect your data:</p>
                <ul>
                  <li>Encryption in transit and at rest</li>
                  <li>Access controls and authentication</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Staff training on data protection</li>
                </ul>
                
                <h3>9. Children's Privacy</h3>
                <p>Our services are not intended for children under 16. We do not knowingly collect personal data from children.</p>
                
                <h3>10. Cookies and Tracking</h3>
                <p>We use cookies and similar technologies for:</p>
                <ul>
                  <li>Essential functionality (no consent required)</li>
                  <li>Analytics and performance (with consent)</li>
                  <li>Marketing and advertising (with consent)</li>
                </ul>
                <p>You can control cookies through your browser settings.</p>
                
                <h3>11. Changes to This Policy</h3>
                <p>We may update this Privacy Policy periodically. We'll notify you of material changes via email or website notice.</p>
                
                <h3>12. Contact Information</h3>
                <p>For privacy questions or to exercise your rights:</p>
                <p><strong>Email:</strong> <a href="mailto:contact@ainastasia.ai" className="text-blue-400 hover:text-blue-300">contact@ainastasia.ai</a><br/>
                <strong>Subject Line:</strong> "Privacy Request" or "Data Protection"</p>
                
                <p className="text-sm text-white/60 mt-8">This Privacy Policy is governed by EU law and complies with the General Data Protection Regulation (GDPR).</p>
              </div>
            ) : (
              <div className="prose prose-invert prose-sm max-w-none">
                <p className="text-lg font-medium mb-6"><strong>Last updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                
                <p><strong>Welcome to AI.Nastasiia!</strong> These Terms of Service ("Terms") govern your use of our AI Tester training platform and services.</p>
                
                <h3>1. Agreement to Terms</h3>
                <p>By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy. If you disagree with any part of these terms, you may not access our services.</p>
                
                <h3>2. Description of Services</h3>
                <h4>Free Demo Service:</h4>
                <ul>
                  <li>Limited access to 12 practice questions from our AI Tester quiz</li>
                  <li>Educational content aligned with ISTQB AI Tester syllabus</li>
                  <li>Progress tracking and instant feedback</li>
                  <li>Email delivery of access credentials and learning materials</li>
                </ul>
                
                <h4>Premium Services:</h4>
                <ul>
                  <li>Full access to 300+ expert-level questions across 11 chapters</li>
                  <li>Comprehensive explanations and detailed feedback</li>
                  <li>Advanced analytics and performance tracking</li>
                  <li>Additional learning resources and materials</li>
                </ul>
                
                <h3>3. Eligibility and Account</h3>
                <ul>
                  <li>You must be at least 16 years old to use our services</li>
                  <li>You must provide accurate and complete information</li>
                  <li>You are responsible for maintaining account security</li>
                  <li>One account per person; no sharing of premium accounts</li>
                </ul>
                
                <h3>4. Acceptable Use</h3>
                <h4>You May:</h4>
                <ul>
                  <li>Use our services for personal professional development</li>
                  <li>Share your quiz results and achievements</li>
                  <li>Provide feedback and suggestions for improvement</li>
                </ul>
                
                <h4>You May Not:</h4>
                <ul>
                  <li>Copy, reproduce, or distribute our question banks or content</li>
                  <li>Reverse engineer or attempt to extract our proprietary content</li>
                  <li>Share premium account credentials with others</li>
                  <li>Use automated tools to access or scrape our content</li>
                  <li>Resell, sublicense, or commercialize our content</li>
                  <li>Upload malicious code or attempt to compromise our systems</li>
                </ul>
                
                <h3>5. Intellectual Property Rights</h3>
                <p>All content, including questions, explanations, code, graphics, and trademarks, is owned by AI.Nastasiia or licensed to us. Our content is protected by copyright, trademark, and other intellectual property laws.</p>
                
                <h3>6. Payment Terms</h3>
                <h4>Premium Services:</h4>
                <ul>
                  <li>Payment processed securely through Gumroad</li>
                  <li>All sales are final unless otherwise stated</li>
                  <li>Prices are subject to change with notice</li>
                  <li>Refunds available within 30 days for unused premium access</li>
                </ul>
                
                <h4>Refund Policy:</h4>
                <ul>
                  <li>Full refund within 7 days if you complete less than 10% of content</li>
                  <li>Partial refund (50%) within 30 days if you complete less than 50% of content</li>
                  <li>No refunds after completing more than 50% of content</li>
                  <li>Technical issues will be resolved or refunded promptly</li>
                </ul>
                
                <h3>7. User-Generated Content</h3>
                <p>If you provide feedback, suggestions, or other content, you grant us a worldwide, royalty-free license to use, modify, and incorporate such content into our services.</p>
                
                <h3>8. Privacy and Data Protection</h3>
                <p>Our collection and use of your information is governed by our Privacy Policy, which is incorporated into these Terms by reference. We are committed to GDPR compliance and protecting your data rights.</p>
                
                <h3>9. Service Availability</h3>
                <ul>
                  <li>We aim for 99.9% uptime but cannot guarantee uninterrupted service</li>
                  <li>Scheduled maintenance will be announced in advance</li>
                  <li>We reserve the right to modify or discontinue services with notice</li>
                  <li>Emergency changes may be made without notice for security reasons</li>
                </ul>
                
                <h3>10. Limitation of Liability</h3>
                <p>TO THE FULLEST EXTENT PERMITTED BY LAW:</p>
                <ul>
                  <li>Our services are provided "as is" without warranties</li>
                  <li>We are not liable for indirect, incidental, or consequential damages</li>
                  <li>Our total liability is limited to the amount you paid for services</li>
                  <li>We do not guarantee exam success or job placement</li>
                </ul>
                
                <h3>11. Indemnification</h3>
                <p>You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of our services or violation of these Terms.</p>
                
                <h3>12. Termination</h3>
                <h4>We May Terminate Access If You:</h4>
                <ul>
                  <li>Violate these Terms or our policies</li>
                  <li>Engage in fraudulent or abusive behavior</li>
                  <li>Fail to pay for premium services</li>
                </ul>
                
                <h4>You May Terminate:</h4>
                <ul>
                  <li>Stop using our services at any time</li>
                  <li>Request account deletion via contact@ainastasia.ai</li>
                  <li>Withdraw consent for marketing communications</li>
                </ul>
                
                <h3>13. Dispute Resolution</h3>
                <p>These Terms are governed by EU law. For EU residents, disputes may be resolved through:</p>
                <ul>
                  <li>Direct negotiation with our support team</li>
                  <li>EU Online Dispute Resolution platform</li>
                  <li>Competent courts in your EU member state</li>
                </ul>
                
                <h3>14. Changes to Terms</h3>
                <p>We may update these Terms periodically. Material changes will be communicated via:</p>
                <ul>
                  <li>Email notification to active users</li>
                  <li>Prominent notice on our website</li>
                  <li>In-app notifications for premium users</li>
                </ul>
                <p>Continued use after changes constitutes acceptance of new Terms.</p>
                
                <h3>15. Severability</h3>
                <p>If any provision of these Terms is found unenforceable, the remaining provisions remain in full force and effect.</p>
                
                <h3>16. Contact Information</h3>
                <p>Questions about these Terms? Contact us:</p>
                <p><strong>Email:</strong> <a href="mailto:contact@ainastasia.ai" className="text-blue-400 hover:text-blue-300">contact@ainastasia.ai</a><br/>
                <strong>Subject Line:</strong> "Terms of Service Inquiry"</p>
                
                <p className="text-sm text-white/60 mt-8">These Terms are designed to be fair and transparent while protecting both your rights and our intellectual property.</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const LegalWatcher = () => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState("privacy");
  
  useEffect(() => {
    const checkURL = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const param = urlParams.get("p");
      
      if (param === "privacy" || param === "terms") {
        setPage(param);
        setOpen(true);
      } else {
        setOpen(false);
      }
    };
    
    // Check URL on mount and when URL changes
    checkURL();
    
    const handleURLChange = () => {
      setTimeout(checkURL, 0); // Delay to ensure URL is updated
    };
    
    // Listen for browser navigation
    window.addEventListener("popstate", handleURLChange);
    
    return () => {
      window.removeEventListener("popstate", handleURLChange);
    };
  }, []);
  
  const handleClose = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("p");
    window.history.replaceState({}, "", url.toString());
    setOpen(false);
  };
  
  return <LegalModal open={open} page={page} onClose={handleClose} />;
};

// -----------------------------
// Sections
// -----------------------------
const Hero = ({ onCTA }) => (
  <section className="relative overflow-hidden pb-20 pt-28 md:pt-32">
    <Glow />
    <LegalWatcher />
    <div className="pointer-events-none absolute inset-0 -z-10 opacity-50 [background:radial-gradient(circle_at_10%_-10%,rgba(255,105,180,.15),transparent_35%),radial-gradient(circle_at_90%_10%,rgba(139,92,246,.18),transparent_40%),radial-gradient(circle_at_50%_120%,rgba(251,191,36,.15),transparent_35%)]" />

    <Container>
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <div className="mb-4 flex flex-wrap gap-3">
            <Badge icon={Stars}>New • 2025 edition</Badge>
            <Badge icon={ShieldCheck}>ISTQB‑aligned practice</Badge>
          </div>

          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-balance bg-gradient-to-br from-white via-white to-white/80 bg-clip-text text-4xl font-extrabold leading-[1.1] text-transparent md:text-6xl">
            Prepare to <span className="relative inline-block">
              <span className="text-fuchsia-300">Tester AI ISTQB Certification</span>
              <span className="absolute inset-0 shimmer-text opacity-30"></span>
            </span>
          </motion.h1>
          

          <p className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-white/80 md:text-lg">
            <span className="text-yellow-300 font-semibold">Exam Simulator | 300+ Real-Exam-Like Questions</span>
            <br/>
            Stop wasting money on basic prep materials that don't prepare you for real exam complexity
          </p>
          
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-4 flex items-center gap-6 text-sm text-white/60">
            <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Interview confidence</span>
            <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Career advancement</span>
            <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Senior-level skills</span>
          </motion.div>

          <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <PrimaryCTA large onClick={onCTA} label="Get Free Demo" />
            <motion.a
              href="https://ainastasia.gumroad.com/l/quiz?wanted=true"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window.trackEvent === 'function') {
                  window.trackEvent('purchase_intent', 'conversion', 'buy_full_version_hero', 29);
                }
                if (typeof window.trackFacebookEvent === 'function') {
                  window.trackFacebookEvent('AddToCart', {
                    content_name: 'Full Version Quiz',
                    content_category: 'Product',
                    value: 29,
                    currency: 'EUR'
                  });
                }
              }}
              whileTap={{ scale: 0.98 }}
              whileHover={{ y: -2, boxShadow: "0 20px 40px rgba(255, 107, 53, 0.4)" }}
              className="gentle-glow group inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 px-6 py-4 font-bold text-white shadow-lg shadow-orange-600/30 transition-all duration-300 hover:shadow-2xl gradient-shift"
            >
              <span className="flex items-center gap-2">
                Buy Full Version
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
            </motion.a>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-start gap-4 text-sm text-white/60">
            <span className="flex items-center gap-1">
              <span className="text-green-400">🔒</span> No commitment • No credit card
            </span>
            <span className="flex items-center gap-1">
              <span className="text-green-400">💯</span> Instant access after signup
            </span>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative aspect-[2/3] md:aspect-[4/3] w-full overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-neutral-900 to-neutral-800 p-4 md:p-6 shadow-2xl">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(/media/1.png)'}} />
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_30%_20%,rgba(255,0,128,.18),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(0,180,255,.18),transparent_45%)] backdrop-blur-sm" />
          <div className="relative z-10 flex h-full flex-col justify-center items-center py-4 md:py-0">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 text-center w-full max-w-lg mx-auto">
              {[
                { k: "300+", v: "Real-Exam Questions" },
                { k: "11", v: "Full Chapters" },
                { k: "Expert", v: "Explanations" },
                { k: "40", v: "Minutes Exam Mode" },
                { k: "5", v: "Languages" },
                { k: "Instant", v: "Results & Analytics" },
              ].map((item, index) => (
                <motion.div 
                  key={item.v} 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 + (0.05 * index) }}
                  whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                  className="aspect-square flex flex-col justify-center items-center rounded-xl border border-white/10 bg-white/5 p-3 md:p-4 backdrop-blur hover-lift pulse-glow"
                >
                  <div className="text-2xl md:text-4xl font-extrabold text-white">{item.k}</div>
                  <div className="text-xs md:text-base font-medium uppercase tracking-wide text-white/90 mt-1">{item.v}</div>
                </motion.div>
              ))}
            </div>
            <div className="mt-4 md:mt-6 hidden sm:flex flex-wrap justify-center gap-2 md:gap-4 text-xs md:text-sm text-white/70">
              <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Aligned to ISTQB AI Tester</span>
              <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Built by QA leaders</span>
              <span className="flex items-center gap-1"><span className="text-green-400">✓</span> Real-world scenarios</span>
            </div>
          </div>
        </motion.div>
      </div>
    </Container>
  </section>
);

const ProofBar = () => (
  <div className="border-y border-white/10 bg-neutral-950/40 py-6 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent shimmer-text"></div>
    <Container className="grid grid-cols-2 gap-6 text-white/80 md:grid-cols-4">
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-3"
      >
        <ShieldCheck className="h-5 w-5 text-fuchsia-300 pulse-glow"/> Aligned to ISTQB AI Tester
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-3"
      >
        <Users className="h-5 w-5 text-fuchsia-300 pulse-glow"/> Built by QA leaders
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-3"
      >
        <Target className="h-5 w-5 text-fuchsia-300 pulse-glow"/> Real‑world scenarios
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        className="flex items-center gap-3"
      >
        <LineChart className="h-5 w-5 text-fuchsia-300 pulse-glow"/> Instant results
      </motion.div>
    </Container>
  </div>
);


const HowItWorks = ({ onCTA }) => (
  <section id="how" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-extrabold text-white md:text-4xl">How it works</h2>
        <p className="mt-3 text-white/70">Three simple steps. No paywall, no fluff.</p>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {[
          { icon: Timer, title: "Tell us where to send it", body: "Enter your email to access 12 real ISTQB AI exam questions for free" },
          { icon: PlayCircle, title: "Get your link instantly", body: "Experience the quality and complexity that sets us apart from basic prep materials" },
          { icon: Award, title: "Go pro when you're hooked", body: "Upgrade to all 300+ questions with personalized explanations for comprehensive exam preparation" },
        ].map((c, i) => (
          <motion.div 
            key={c.title} 
            initial={{ y: 16, opacity: 0 }} 
            whileInView={{ y: 0, opacity: 1 }} 
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
            viewport={{ once: true, amount: 0.4 }} 
            transition={{ duration: 0.5, delay: i * 0.06 }} 
            className="group rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-6 text-white/90 backdrop-blur hover:border-fuchsia-400/30 hover-lift hover:shadow-2xl hover:shadow-fuchsia-500/20"
          >
            <c.icon className="mb-4 h-8 w-8 text-fuchsia-300" />
            <h3 className="text-lg font-bold">{c.title}</h3>
            <p className="mt-2 text-white/70">{c.body}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-10 text-center">
        <PrimaryCTA large onClick={onCTA} label="Get Free Demo" />
      </div>
    </Container>
  </section>
);


const CompetitiveAdvantage = () => (
  <section id="advantage" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="mx-auto max-w-4xl text-center">
        <h2 className="text-3xl font-extrabold text-white md:text-4xl">Why This Beats Other ISTQB AI Prep Materials</h2>
        <p className="mt-3 text-white/70">I bought all the competing products. Here's what I found vs what I built:</p>
        
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <motion.div 
            whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
            className="rounded-3xl border border-red-500/30 bg-red-500/10 p-6 hover-lift hover:shadow-2xl hover:shadow-red-500/20 hover:border-red-400/50 transition-all duration-300"
          >
            <h3 className="text-lg font-bold text-red-300 mb-4">Other Products</h3>
            <ul className="space-y-3 text-white/70 text-left">
              <li className="flex items-center gap-3"><span className="text-red-400">✗</span> 40-120 basic questions</li>
              <li className="flex items-center gap-3"><span className="text-red-400">✗</span> €40+ for simple Q&A</li>
              <li className="flex items-center gap-3"><span className="text-red-400">✗</span> English only</li>
              <li className="flex items-center gap-3"><span className="text-red-400">✗</span> Definition-focused</li>
              <li className="flex items-center gap-3"><span className="text-red-400">✗</span> No detailed explanations</li>
              <li className="flex items-center gap-3"><span className="text-red-400">✗</span> Static content</li>
            </ul>
          </motion.div>
          
          <motion.div 
            whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
            className="rounded-3xl border border-green-500/30 bg-green-500/10 p-6 hover-lift hover:shadow-2xl hover:shadow-green-500/20 hover:border-green-400/50 transition-all duration-300"
          >
            <h3 className="text-lg font-bold text-green-300 mb-4">Our Product</h3>
            <ul className="space-y-3 text-white/70 text-left">
              <li className="flex items-center gap-3"><span className="text-green-400">✓</span> 300+ real-exam complexity</li>
              <li className="flex items-center gap-3"><span className="text-green-400">✓</span> Better pricing for premium content</li>
              <li className="flex items-center gap-3"><span className="text-green-400">✓</span> 5 languages supported</li>
              <li className="flex items-center gap-3"><span className="text-green-400">✓</span> Practical scenarios included</li>
              <li className="flex items-center gap-3"><span className="text-green-400">✓</span> Personalized explanations for each wrong answer</li>
              <li className="flex items-center gap-3"><span className="text-green-400">✓</span> Real-world AI testing situations</li>
            </ul>
          </motion.div>
          
          <motion.div 
            whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
            className="rounded-3xl border border-fuchsia-500/30 bg-fuchsia-500/10 p-6 hover-lift hover:shadow-2xl hover:shadow-fuchsia-500/20 hover:border-fuchsia-400/50 transition-all duration-300"
          >
            <h3 className="text-lg font-bold text-fuchsia-300 mb-4">The Reality</h3>
            <ul className="space-y-3 text-white/80 text-left">
              <li className="flex items-start gap-3"><span className="text-fuchsia-400 mt-1">→</span> <span>No other product has this depth</span></li>
              <li className="flex items-start gap-3"><span className="text-fuchsia-400 mt-1">→</span> <span>Created by someone who tested all alternatives</span></li>
              <li className="flex items-start gap-3"><span className="text-fuchsia-400 mt-1">→</span> <span>Built for global accessibility</span></li>
            </ul>
          </motion.div>
        </div>
      </div>
    </Container>
  </section>
);

const ForWhom = () => (
  <section id="for" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <h2 className="text-3xl font-extrabold text-white md:text-4xl">Who is this for?</h2>
          <p className="mt-3 text-white/70">QA professionals preparing for ISTQB AI Testing certification:</p>
          <ul className="mt-5 grid gap-2 text-white/85">
            {[
              "QA Engineers with ISTQB Foundation Level seeking AI specialization",
              "QA Leads expanding team capabilities into AI testing",
              "Manual and Automation QA transitioning to AI testing",
              "Technical professionals requiring ISTQB AI certification for career advancement",
              "QA professionals working in companies implementing AI solutions",
            ].map((li) => (
              <li key={li} className="flex items-center gap-3">
                <Rocket className="h-4 w-4 text-fuchsia-300" /> {li}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative rounded-3xl border border-white/10 bg-white/5 p-6 text-white/80 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(/media/3.png)'}} />
          <div className="absolute inset-0 bg-white/5 backdrop-blur-sm" />
          <div className="relative z-10">
          <h3 className="text-lg font-bold text-white">What You Get in the Free Demo</h3>
          <p className="mt-2">Experience real ISTQB AI exam complexity - <span className="text-green-300 font-semibold">completely risk-free</span>:</p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {[
              { k: "12 Questions", v: "COVERS KEY EXAM TOPICS" },
              { k: "Simple Exam Simulator", v: "INSTANT DETAILED ANALYSIS" },
              { k: "Detailed Explanations", v: "PERSONALIZED SCORE + INSIGHTS" },
              { k: "2 basic chapters", v: "for demo" },
            ].map((x) => (
              <motion.div 
                key={x.v} 
                whileHover={{ scale: 1.05, y: -4 }}
                className="rounded-2xl border border-white/10 bg-neutral-900/60 p-4 text-center hover:bg-neutral-900/80 hover:border-green-400/30 transition-all duration-300 cursor-pointer"
              >
                <div className="text-lg font-extrabold text-white">{x.k}</div>
                <div className="text-xs uppercase tracking-wide text-white/60">{x.v}</div>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <motion.span 
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 bg-green-500/20 border border-green-400/30 rounded-full px-3 py-1 text-sm font-medium text-green-300 hover:bg-green-500/30 hover:border-green-400/50 transition-all duration-300 cursor-default"
            >
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              No commitment • No credit card
            </motion.span>
          </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

const WhyNow = () => (
  <section id="whynow" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-extrabold text-white md:text-4xl">Why now?</h2>
        <p className="mt-3 text-white/70">
          AI testing skills are the new leverage. Teams that catch bias, drift, and prompt failures early move faster — and ship safer.
          Take the demo now; feel your blind spots within minutes.
        </p>
      </div>
    </Container>
  </section>
);

const ScreensShowcase = () => (
  !ASSETS.screens.length ? null : (
    <section id="screens" className="relative scroll-mt-24 py-20">
      <Glow />
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold text-white md:text-4xl">Peek inside</h2>
          <p className="mt-3 text-white/70">Real UI, real questions, real feedback. Mobile‑first and fast.</p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ASSETS.screens.map((src, i) => (
            <div key={i} className="overflow-hidden rounded-3xl border border-white/10 bg-white/5">
              <img src={src} alt={`AI Tester screen ${i+1}`} className="h-full w-full object-cover" onError={(e)=>{e.currentTarget.closest('div').style.display='none';}} />
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
);

const PricingTeaser = ({ onCTA }) => (
  <section id="upgrade" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="mx-auto max-w-4xl text-center mb-12">
        <h2 className="text-4xl font-extrabold text-white md:text-6xl">
          <span className="text-yellow-300">Shut Up</span> and Take My Money!
        </h2>
        <p className="mt-4 text-lg text-white/80">If you're already familiar with me and my products and don't need confirmation that they're f*cking awesome, I suggest you buy full access right away.</p>
      </div>
      
      <div className="grid items-center gap-10 md:grid-cols-2">
        <div>
          <h3 className="text-3xl font-extrabold text-white md:text-4xl">What Will I Get?</h3>
          <p className="mt-3 text-white/70"><strong>Zero fluff, maximum impact</strong> — this isn't another boring certification prep. It's your fast-track to ISTQB AI Tester certification.</p>
          <ul className="mt-5 grid gap-2 text-white/85">
            {[
              "300+ battle-tested questions from real exams",
              "11 complete chapters covering all ISTQB AI topics", 
              "Expert explanations that actually teach - learn from detailed answers",
              "40-minute exam simulation mode",
              "Available in 5 languages for global accessibility",
              "Lifetime access with all future updates"
            ].map((li) => (
              <li key={li} className="flex items-center gap-3">
                <CheckCircle className="h-4 w-4 text-green-400"/> {li}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <motion.a
              href="https://ainastasia.gumroad.com/l/quiz?wanted=true"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof window.trackEvent === 'function') {
                  window.trackEvent('purchase_intent', 'conversion', 'buy_full_version_pricing', 29);
                }
                if (typeof window.trackFacebookEvent === 'function') {
                  window.trackFacebookEvent('Purchase', {
                    content_name: 'Full Version Quiz',
                    content_category: 'Product',
                    value: 29,
                    currency: 'EUR'
                  });
                }
              }}
              whileTap={{ scale: 0.98 }}
              whileHover={{ y: -2, boxShadow: "0 20px 40px rgba(255, 107, 53, 0.4)" }}
              className="gentle-glow group relative inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 px-8 py-5 text-lg md:text-xl min-w-[280px] font-bold text-white shadow-lg shadow-orange-600/30 ring-0 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 hover:shadow-2xl gradient-shift"
            >
              <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent opacity-20" />
              <span className="absolute inset-0 rounded-2xl bg-white/0 group-hover:bg-white/10 transition-colors duration-300" />
              <span className="relative flex items-center gap-3 font-bold tracking-wide">
                <Zap className="h-5 w-5 group-hover:animate-pulse" />
                Get Full Version Now
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
            </motion.a>
          </div>
        </div>
        
        <div className="relative rounded-3xl border border-white/10 bg-white/5 p-6 text-white/80 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(/media/4.png)'}} />
          <div className="absolute inset-0 bg-white/5 backdrop-blur-sm" />
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-white mb-4">Key Benefits</h3>
            <p className="mt-2 mb-4 text-white/70">Ready to upgrade from the demo? <span className="text-green-300 font-semibold">Here's what makes it worth it</span>:</p>
            <div className="grid gap-3 grid-cols-2">
              {[
                { k: "Real Exam", v: "COMPLEXITY & DEPTH" },
                { k: "Expert Level", v: "DETAILED EXPLANATIONS" },
                { k: "Multi-Language", v: "GLOBAL ACCESSIBILITY" },
                { k: "Lifetime", v: "ACCESS & UPDATES" },
              ].map((x) => (
                <motion.div 
                  key={x.v} 
                  whileHover={{ scale: 1.05, y: -3 }}
                  className="rounded-2xl border border-white/10 bg-neutral-900/60 p-4 text-center hover:bg-neutral-900/80 hover:border-orange-400/30 transition-all duration-300"
                >
                  <div className="text-lg font-extrabold text-white">{x.k}</div>
                  <div className="text-xs uppercase tracking-wide text-white/60">{x.v}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

const CreatorCredibility = () => (
  <section id="creator" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="mx-auto max-w-4xl">
        <div className="grid items-center gap-8 rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 md:grid-cols-3">
          <div className="md:col-span-1 text-center">
            <div className="mx-auto w-32 h-32 rounded-full bg-gradient-to-br from-fuchsia-500 via-violet-600 to-cyan-500 p-1 mb-4">
              <div className="w-full h-full rounded-full bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(/media/2.jpeg)'}} />
            </div>
            <h3 className="text-xl font-bold text-white">Anastasia Babanina</h3>
            <p className="text-sm text-white/60 mt-1">QA Tech Lead Gaming Industry</p>
            <div className="mt-4 space-y-2">
              <motion.div 
                whileHover={{ scale: 1.1, y: -2 }}
                className="inline-block bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-xs text-white/90 hover:bg-white/15 hover:border-white/30 transition-all duration-300 cursor-default"
              >
                10+ Years QA Experience
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.1, y: -2 }}
                className="inline-block bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-xs text-white/90 hover:bg-white/15 hover:border-white/30 transition-all duration-300 cursor-default"
              >
                2+ Years Working with AI Projects
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.1, y: -2 }}
                className="inline-block bg-white/10 border border-white/20 rounded-lg px-3 py-1 text-xs text-white/90 hover:bg-white/15 hover:border-white/30 transition-all duration-300 cursor-default"
              >
                Leading Multiple QA Teams
              </motion.div>
            </div>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold text-white mb-4">Who Am I?</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              Hi! I'm a QA Tech Lead who got tired of boring certification prep 😴
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              For the past two years, I've been neck-deep in AI projects, wrestling with models that think they're smarter than they are. After failing to find decent ISTQB AI prep materials (seriously, 40 questions for €40? Come on! 🙄), I decided to create what I wished existed.
            </p>
            <p className="text-white/80 leading-relaxed mb-4">
              This simulator is the result of 2 months of late-night coding, AI wrestling, and probably too much coffee. Each question is crafted to make you think, not just memorize. Because let's be honest - the real exam won't ask you to recite definitions! ☕
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

const FAQ = ({ onCTA }) => (
  <section id="faq" className="relative scroll-mt-24 py-20">
    <Glow />
    <Container>
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-extrabold text-white md:text-4xl">FAQ</h2>
        <p className="mt-3 text-white/70">Still curious? We’ve got you.</p>
      </div>

      <div className="mx-auto mt-10 grid max-w-3xl gap-4">
        {[
          { q: "What makes this different from other ISTQB prep?", a: "300+ real-exam complexity questions vs typical 40 basic ones. Each question requires genuine AI testing knowledge, not just memorization." },
          { q: "Is this the official ISTQB exam?", a: "No, this is preparation training. The official exam is through ISTQB providers. We simulate real exam difficulty to prepare you properly." },
          { q: "What makes your questions 'real-exam' quality?", a: "Each question is crafted following ISTQB exam structure with expert-level distractors that require genuine AI testing knowledge to distinguish correct answers." },
          { q: "Do I need to sign up?", a: "Yes, we need your email to send the demo link. Takes 30 seconds." },
          { q: "Is the demo free?", a: "Yes, completely free. No credit card required." },
          { q: "How does this compare to other ISTQB AI prep materials?", a: "Our simulator offers 300+ questions vs competitors' typical 40-120. We focus on real exam scenarios and practical applications, not just memorizing definitions. Plus, we provide detailed explanations for every wrong answer and support 5 languages." },
          { q: "Do I need deep ML knowledge?", a: "No. We focus on practical testing concepts that QA engineers need to know." },
          { q: "How many questions in the full version?", a: "300+ questions covering all 11 chapters of the ISTQB AI Tester syllabus." },
          { q: "Can I retake the quiz?", a: "Yes, unlimited attempts in both demo and full version. Questions are randomized each time." },
          { q: "Do I get a certificate?", a: "You get a completion badge. For official ISTQB certification, you need to take the proctored exam." },
          { q: "What's the difference between demo and full?", a: "Demo: 12 carefully selected questions with easy difficulty. Full: 300+ randomized exam-level questions, detailed analytics, exam mode, lifetime access." },
          { q: "Is there a money-back guarantee?", a: "Yes, 7-day no-questions-asked refund if you're not satisfied with the full version." },
          { q: "Can I access on mobile?", a: "Yes, fully responsive design works on all devices - phone, tablet, desktop." },
        ].map((f, i) => (
          <motion.details 
            key={i} 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="group rounded-2xl border border-white/10 bg-white/5 p-5 text-white/90 hover-lift hover:shadow-xl hover:shadow-fuchsia-500/10 hover:border-fuchsia-400/30 transition-all duration-300"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between text-left font-semibold hover:text-fuchsia-300 transition-colors">
              {f.q}
              <span className="ml-4 rounded-md bg-white/10 px-2 py-0.5 text-xs text-white/70 group-hover:bg-fuchsia-500/20 transition-colors">open</span>
            </summary>
            <p className="mt-3 text-white/70">{f.a}</p>
          </motion.details>
        ))}
      </div>

      <div className="mt-10 text-center">
        <PrimaryCTA large onClick={onCTA} label="Get Free Demo" />
        <p className="mt-2 text-sm text-white/60">The access check is quick. Right after it, you'll get the demo link.</p>
      </div>

      {/* Course Pre-order Section - Redesigned */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mt-20 relative overflow-hidden"
      >
        {/* Animated gradient background */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-violet-600/20 via-fuchsia-500/20 to-orange-500/20 gradient-shift" />
        
        {/* Glowing orbs for visual interest */}
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-purple-500/30 rounded-full blur-3xl floating" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-orange-500/30 rounded-full blur-3xl floating" style={{animationDelay: '2s'}} />
        
        {/* Main content container */}
        <div className="relative p-12 rounded-3xl border border-white/20 backdrop-blur-sm bg-black/30">
          {/* Top accent line */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-fuchsia-400 to-transparent shimmer-text" />
          
          <div className="text-center space-y-6">
            {/* Icon with glow effect */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 backdrop-blur gentle-glow"
            >
              <Brain className="w-12 h-12 text-white" />
            </motion.div>
            
            {/* Main heading with gradient text */}
            <motion.h3 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-3xl md:text-4xl font-extrabold"
            >
              <span className="bg-gradient-to-r from-purple-300 via-pink-300 to-orange-300 bg-clip-text text-transparent">
                🎓 Ready for Deep AI Testing Knowledge?
              </span>
            </motion.h3>
            
            {/* Description with better typography */}
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-lg text-white/90 leading-relaxed max-w-3xl mx-auto"
            >
              Master AI Testing Theory with our comprehensive course. 
              <span className="block mt-2 text-white/70">
                Near <span className="text-fuchsia-300 font-bold">70+ micro-lessons</span> covering everything from neural networks to bias detection. 
                Learn through analogies and real-world scenarios. 
                Perfect for learners who want more than just exam prep!
              </span>
            </motion.p>
            
            {/* Feature badges */}
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {[
                "70+ Micro-lessons",
                "Real-world Scenarios", 
                "Neural Networks",
                "Bias Detection"
              ].map((feature, i) => (
                <motion.span
                  key={feature}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + i * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-sm text-white/80 backdrop-blur hover:bg-white/15 transition-all"
                >
                  ✨ {feature}
                </motion.span>
              ))}
            </motion.div>
            
            {/* CTA Button with premium feel */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <motion.a 
                href="https://ainastasia.mykajabi.com/pl/2148634864" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => {
                  if (typeof window.trackEvent === 'function') {
                    window.trackEvent('early_bird_signup', 'engagement', 'join_early_bird_list', 1);
                  }
                  if (typeof window.trackFacebookEvent === 'function') {
                    window.trackFacebookEvent('Subscribe', {
                      content_name: 'Early Bird List',
                      content_category: 'Newsletter',
                      value: 1
                    });
                  }
                }}
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.98 }}
                className="group relative inline-flex items-center justify-center px-10 py-5 text-lg font-bold overflow-hidden rounded-2xl"
              >
                {/* Animated gradient background */}
                <span className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 gradient-shift" />
                
                {/* Shine effect on hover */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 shimmer-text" />
                
                {/* Button content */}
                <span className="relative flex items-center gap-3 text-black">
                  <Rocket className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  Join Early Bird List
                  <Stars className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                </span>
              </motion.a>
              
              {/* Urgency indicator */}
              <motion.p 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-4 flex items-center justify-center gap-2 text-sm"
              >
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                </span>
                <span className="text-orange-300">Early bird pricing ends soon</span>
                <span className="text-white/40">•</span>
                <span className="text-white/60">Instant access when launched</span>
              </motion.p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </Container>
  </section>
);

const Footer = ({ onCTA, authEnabled }) => (
  <footer className="relative border-t border-white/10 py-12">
    <Container className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
      <div className="flex items-center gap-3">
        <Logo />
        <div>
          <div className="text-sm font-semibold tracking-wide text-white/70">AI.NASTASIIA</div>
          <p className="mt-1 text-xs text-white/50">© {new Date().getFullYear()} • Independent training aligned with ISTQB AI Tester outcomes.</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-4">
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a 
            href="https://instagram.com/ai.nastasia" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
            title="Instagram"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
            </svg>
          </a>
          <a 
            href="https://www.linkedin.com/in/ainastasia/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
            title="LinkedIn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a 
            href="https://t.me/ai_nastasia" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
            title="Telegram"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18.717-1.877 7.56-2.349 9.008-.197.61-.592.809-.973.809-.329 0-.658-.097-1.04-.291-1.185-.603-2.379-1.215-3.541-1.81-.532-.272-.969-.496-.969-.746 0-.184.147-.366.368-.544.698-.561 1.537-1.231 2.245-1.8 1.027-.825 1.75-1.407 1.753-1.803.002-.256-.256-.256-.513-.128-1.296.65-3.867 2.03-5.019 2.624-.348.179-.663.268-.945.268-.311 0-.911-.138-1.357-.265-.538-.154-.968-.235-.93-.496.019-.135.232-.273.639-.415 2.518-.692 4.199-1.15 5.032-1.371 2.379-.632 2.875-.742 3.197-.742.072 0 .233.01.337.062a.39.39 0 0 1 .124.15.874.874 0 0 1 .014.217c-.004.06-.006.124-.009.186z"/>
            </svg>
          </a>
          <a 
            href="https://www.youtube.com/@ainastasiia" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white transition-colors"
            title="YouTube"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <button 
            onClick={() => window.location.href = '?p=privacy'} 
            className="text-sm text-white/60 hover:text-white underline"
          >
            Privacy
          </button>
          <button 
            onClick={() => window.location.href = '?p=terms'} 
            className="text-sm text-white/60 hover:text-white underline"
          >
            Terms
          </button>
          <PrimaryCTA large onClick={onCTA} label="Try Free Demo" />
        </div>
      </div>
    </Container>
  </footer>
);

// -----------------------------
// Page component
// -----------------------------
export default function AITesterLanding() {
  const supabase = getSupabase();
  const authEnabled = AUTH_MODE === "supabase" && !!supabase;
  const [showTypeform, setShowTypeform] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  // Отключаем анимации на мобильных устройствах при инициализации
  useEffect(() => {
    if (isMobileDevice()) {
      document.body.classList.add('mobile-no-animations');
    }
  }, []);

  useEffect(() => {
    // font
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;800&display=swap';
    document.head.appendChild(link);
    document.documentElement.style.fontFamily = 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
  }, []);

  useEffect(() => {
    // tiny runtime tests (non-blocking)
    try {
      console.assert(withUTM('https://example.com').startsWith('https://'), 'withUTM should return a URL');
      console.assert(typeof sha256Hex === 'function', 'sha256Hex exists');
    } catch {}
  }, []);

  const handleCTA = () => {
    // Track CTA clicks
    if (typeof window.trackEvent === 'function') {
      window.trackEvent('cta_click', 'engagement', 'get_free_demo', 1);
    }
    if (typeof window.trackFacebookEvent === 'function') {
      window.trackFacebookEvent('InitiateCheckout', {
        content_name: 'Free Demo CTA',
        content_category: 'Demo Access',
        value: 0
      });
    }
    
    // Use our simple email modal instead of Typeform/Auth
    setShowEmailModal(true);
  };

  const handleEmailSubmit = async (email, marketingConsent) => {
    console.log('🔄 Email submit started:', { email, marketingConsent });
    setShowEmailModal(false);
    
    // Track successful demo access
    if (typeof window.trackEvent === 'function') {
      window.trackEvent('demo_access_granted', 'conversion', 'landing_page_email_submit', 1);
    }
    if (typeof window.trackFacebookEvent === 'function') {
      window.trackFacebookEvent('CompleteRegistration', {
        content_name: 'Demo Quiz Access',
        content_category: 'Registration',
        value: 1
      });
    }
    
    // Immediately redirect to demo quiz - don't wait for backend  
    // Try both normal path and hash fallback
    const demoUrl = `${window.location.origin}/demoquiz?debug=fixed_${Date.now()}`;
    const demoHashUrl = `${window.location.origin}/#/demoquiz?debug=fixed_${Date.now()}`;
    console.log('🎯 Redirecting to:', demoUrl, 'fallback:', demoHashUrl);
    
    // Backend call is now handled by EmailCaptureModal component
    
    // Redirect immediately - try multiple methods
    console.log('🚀 Starting redirect...');
    
    // First try normal path
    window.location.href = demoUrl;
  };

  return (
    <main className="min-h-screen bg-neutral-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-neutral-950/60 backdrop-blur">
        <Container className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <Logo />
            <div className="text-sm font-semibold tracking-wide text-white/90">AI Tester Quiz</div>
          </div>
          <PrimaryCTA onClick={handleCTA} className="inline-flex" label="Get Free Demo" />
        </Container>
      </header>

      {/* Body sections */}
      <Hero onCTA={handleCTA} />
      <ProofBar />
      <HowItWorks onCTA={handleCTA} />
      <CompetitiveAdvantage />
      <ForWhom />
      {/* WhyNow section removed per user request */}
      <PricingTeaser onCTA={handleCTA} />
      <CreatorCredibility />
      <FAQ onCTA={handleCTA} />
      <Footer onCTA={handleCTA} authEnabled={authEnabled} />

      {/* Modals */}
      <TypeformModal open={showTypeform} onClose={() => setShowTypeform(false)} />
      {authEnabled && (
        <AuthModal
          open={showAuth}
          onClose={() => setShowAuth(false)}
          supabase={supabase}
          onTracked={(n, p) => insertEvent(supabase, { name: n, data: p })}
        />
      )}
      
      {/* Simple Email Capture Modal */}
      <EmailCaptureModal 
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSubmit={handleEmailSubmit}
      />
    </main>
  );
}
